import makePlainReducer from '../makePlainReducer';

/**
 * Creates a `byId` reducer. A `byId` reducer holds all info about every fetched
 * item for a model keyed by item id. It is tightly coupled to the
 * [allIds](#makeallidsreducer) reducer.
 *
 * * **Initial State**: `{}`
 * * **Supported Actions**:
 *   * `GET_<MODEL>`: Adds the new item to the reducer. If it already exists,
 *     the reducer will be updated with the data in the action
 *     * `action.data`: Object holding all of the item's data
 *   * `GET_<MODEL>_SUCCESS`: Same as `GET_<MODEL>`
 *   * `CREATE_<MODEL>`: Same as `GET_<MODEL>`
 *   * `CREATE_<MODEL>_SUCCESS`: Same as `GET_<MODEL>`
 *   * `UPDATE_<MODEL>`: Same as `GET_<MODEL>`
 *   * `UPDATE_<MODEL>_SUCCESS`: Same as `GET_<MODEL>`
 *   * `GET_<MODEL>S`: Adds all new items to the reducer, updating them if they
 *     already exist.
 *     * `action.data`: Array holding all of the items
 *   * `GET_<MODEL>S_SUCCESS`: Same as `GET_<MODEL>S`
 *   * `CREATE_<MODEL>S`: Same as `GET_<MODEL>S`
 *   * `CREATE_<MODEL>S_SUCCESS`: Same as `GET_<MODEL>S`
 *   * `UPDATE_<MODEL>S`: Same as `GET_<MODEL>S`
 *   * `UPDATE_<MODEL>S_SUCCESS`: Same as `GET_<MODEL>S`
 *   * `DESTROY_<MODEL>_SUCCESS`: Removes the item from the `byId` object
 *     * `action.id`: The id of the destroyed item
 *   * `REMOVE_TOKEN`: Resets the reducer to the initial state.
 *
 * @param {Object} options The options for the creator function
 * @param {string} options.model The name of the model being used
 * @param {string} options.pluralModel The plural name of the model being used
 * @param {Object} options.customConfig Custom action handlers
 * @returns {Function} A newly created redux reducer
 *
 * @public
 * @author Connor Taylor
 *
 * @example
 * import { combineReducers } from 'redux';
 *
 * const reducer = combineReducers({
 *   byId: makeByIdReducer({
 *     model: `article`,
 *     customConfig: {
 *       CUSTOM_ACTION: (state, action) => ({ foo: `bar` }),
 *     },
 *   }),
 * });
 *
 * // An example of what a byId reducer could look like in practice
 * {
 *   article: {
 *     allIds: [1, 2],
 *     byId: {
 *       1: { id: 1, url: `http://www.google.com/foo`, publishDate: `2018-01-15`, featured: false },
 *       2: { id: 2, url: `http://www.google.com/bar`, publishDate: `2018-01-16` featured: true },
 *     }
 *   }
 * }
 *
 */
const makeByIdReducer = ({
  model,
  pluralModel = null,
  customConfig = {},
  initialState = null,
}) => {
  const upperModel = model.toUpperCase();
  const pluralVersion = pluralModel || `${model}s`;
  const upperPluralModel = pluralVersion.toUpperCase();

  const singleAddition = (state, action) => {
    const existingData = state[action.data.id] ? state[action.data.id] : {};

    return {
      ...state,
      [action.data.id]: { ...existingData, ...action.data },
    };
  };
  const batchAddition = (state, action) =>
    action.data.reduce(
      (newState, item) => ({
        ...newState,
        [item.id]: { ...newState[item.id], ...item },
      }),
      state,
    );
  const removal = (state, action) => {
    const newState = { ...state };
    delete newState[action.id];

    return newState;
  };

  return makePlainReducer({
    model,
    config: {
      // Single additions
      [`GET_${upperModel}`]: singleAddition,
      [`GET_${upperModel}_SUCCESS`]: singleAddition,
      [`CREATE_${upperModel}`]: singleAddition,
      [`CREATE_${upperModel}_SUCCESS`]: singleAddition,
      [`UPDATE_${upperModel}`]: singleAddition,
      [`UPDATE_${upperModel}_SUCCESS`]: singleAddition,
      // Batch additions
      [`GET_${upperPluralModel}`]: batchAddition,
      [`GET_${upperPluralModel}_SUCCESS`]: batchAddition,
      [`CREATE_${upperPluralModel}`]: batchAddition,
      [`CREATE_${upperPluralModel}_SUCCESS`]: batchAddition,
      [`UPDATE_${upperPluralModel}`]: batchAddition,
      [`UPDATE_${upperPluralModel}_SUCCESS`]: batchAddition,

      [`DESTROY_${upperModel}`]: removal,
      [`DESTROY_${upperModel}_SUCCESS`]: removal,
      REMOVE_TOKEN: () => initialState || {},
      ...customConfig,
    },
    initialState: initialState || {},
  });
};

/** @component */
export default makeByIdReducer;
